import { captureSessionEpoch, isSessionEpochCurrent } from './sessionEpoch';

export interface ApiErrorPayload {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiError extends Error {
  constructor(readonly status: number, readonly payload: ApiErrorPayload) {
    super(payload.message);
    this.name = 'ApiError';
  }
}

interface ApiRequestInit extends RequestInit {
  suppressAuthExpiredEvent?: boolean;
  responseType?: 'json' | 'blob';
}

export async function apiRequest<T>(path: string, init: ApiRequestInit = {}): Promise<T> {
  const requestEpoch = captureSessionEpoch();
  const { suppressAuthExpiredEvent = false, responseType = 'json', ...fetchInit } = init;
  const headers = new Headers(init.headers);
  if (init.body != null && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const response = await fetch(path, {
    ...fetchInit,
    cache: init.cache ?? 'no-store',
    credentials: 'same-origin',
    headers,
  });
  if (!response.ok) {
    let payload: ApiErrorPayload = { code: 'REQUEST_FAILED', message: `请求失败 (${response.status})` };
    try {
      const body = await response.json();
      if (typeof body?.error?.code==='string' && typeof body.error.message==='string') payload=body.error;
      else if (typeof body?.code==='string' && typeof body.message==='string') payload={code:body.code,message:body.message};
    } catch {
      // Keep the generic error when the proxy returns a non-JSON page.
    }
    if (response.status === 401 && !suppressAuthExpiredEvent && isSessionEpochCurrent(requestEpoch)) {
      window.dispatchEvent(new CustomEvent('cloud-auth-expired'));
    }
    throw new ApiError(response.status, payload);
  }
  if (response.status === 204) return undefined as T;
  if (responseType === 'blob') return response.blob() as Promise<T>;
  return response.json() as Promise<T>;
}

export function jsonBody(value: unknown): RequestInit {
  return { body: JSON.stringify(value) };
}
